import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Typeahead from "$components/form-inputs/typeahead";
import MultiSelect from "$components/form-inputs/select/multi";
import { AppContextStore } from "$stores/app-context";
import { CrmStore, search as crmSearch } from "$stores/crm-filter";
import { ClientFilterStore, search as clientSearch } from "$stores/client-filter";
import { getTerm } from "$stores/dictionary";

// value: { items: [{ value, text }] }
const SelectTargetStep = ({ focusType, value, onChange }) => {
    const items = value?.items || [];

    const focusTypeTitles = {
        PartnerClients: `Select a ${getTerm("parentCrm")}`,
        PracticeArea:   `Select a ${getTerm("serviceLine")}`,
        Industry:       `Select a ${getTerm("industry")}`,
        SelectClients:  `Select ${getTerm("parentClients")}`,
        MatterType:     `Select a ${getTerm("projectType")}`,
    };

    const removeItem = (itemValue) => {
        onChange({ items: items.filter((i) => i.value !== itemValue) });
    };

    return (
        <div className="wizard-step">
            <h3>{focusTypeTitles[focusType] || "Select Target"}</h3>
            {items.length > 0 && (
                <ul className="wizard-selected-list">
                    {items.map((item) => (
                        <li key={item.value}>
                            <span>{item.text}</span>
                            <button
                                type="button"
                                className="wizard-selected-remove"
                                onClick={() => removeItem(item.value)}
                                aria-label={`Remove ${item.text}`}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <SelectTargetBody
                focusType={focusType}
                items={items}
                onReplace={(newItems) => onChange({ items: newItems })}
            />
        </div>
    );
};

const SelectTargetBody = ({ focusType, items, onReplace }) => {
    const { crmOptions, crmLoading } = CrmStore.useState((f) => ({
        crmOptions: f.results,
        crmLoading: f.isLoading,
    }));
    const { clientOptions, clientLoading } = ClientFilterStore.useState((f) => ({
        clientOptions: f.results,
        clientLoading: f.isLoading,
    }));
    const filterMenu = AppContextStore.useState((s) => s.filterMenu);
    const industryOptions = filterMenu.find((f) => f.key === "industry")?.values || [];
    const practiceOptions = filterMenu.find((f) => f.key === "practice")?.values || [];
    const matterTypeOptions = filterMenu.find((f) => f.key === "matterType")?.values || [];

    switch (focusType) {
        case "PartnerClients":
            return (
                <Typeahead
                    name="partnerSelect"
                    placeholder="Search by name..."
                    label={getTerm("parentCrm")}
                    options={crmOptions}
                    value={items}
                    optionLookup={crmSearch}
                    apiLoading={crmLoading}
                    updateOnChange={(name, values) => onReplace(values || [])}
                    hideSelectedFromDropdown={true}
                    showSelectAndDeselectAll={false}
                />
            );

        case "PracticeArea":
            return (
                <MultiSelect
                    name="practiceSelect"
                    label={getTerm("serviceLine")}
                    options={practiceOptions}
                    value={items}
                    updateOnChange={(name, values) => onReplace(values || [])}
                />
            );

        case "Industry":
            return (
                <MultiSelect
                    name="industrySelect"
                    label={getTerm("industry")}
                    options={industryOptions}
                    value={items}
                    updateOnChange={(name, values) => onReplace(values || [])}
                />
            );

        case "SelectClients":
            return (
                <Typeahead
                    name="clientSelect"
                    placeholder={`Search ${getTerm("parentClients").toLowerCase()}...`}
                    label={getTerm("parentClients")}
                    options={clientOptions}
                    value={items}
                    optionLookup={clientSearch}
                    apiLoading={clientLoading}
                    updateOnChange={(name, values) => onReplace(values || [])}
                    hideSelectedFromDropdown={true}
                    showSelectAndDeselectAll={false}
                />
            );

        case "MatterType":
            return (
                <MultiSelect
                    name="matterTypeSelect"
                    label={getTerm("projectType")}
                    options={matterTypeOptions}
                    value={items}
                    updateOnChange={(name, values) => onReplace(values || [])}
                />
            );

        default:
            return <p>Unknown focus type: {focusType}</p>;
    }
};

export default SelectTargetStep;
